// =============================================
// Layer 6 — LLM Semantic Audit (gpt-4.1-mini, ~$0.003/art)
// =============================================
// Wywołuje prompt 'content_audit_llm' z DB (thematic_prompts) ze structured JSON output.
// Sprawdza 4 wymiary: stays_on_topic, halucynacje (vs KG), voice_natural, claim_density.
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { callLLMForStage, parseLLMJSON } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { logPublic, logDebug } from "@/services/contentEngine/contentLogger";

export interface LlmAuditResult {
  status: "ok" | "warning" | "failed";
  overall_status: "passed" | "warning" | "failed";
  semantic_check: {
    stays_on_topic: boolean;
    halucinations: Array<{
      claim: string;
      type: "date_attribution" | "name_invention" | "metric_fabrication" | "quote_attribution";
      severity: "high" | "medium" | "low";
    }>;
    voice_natural: boolean;
    voice_issues: string[];
    claim_density: "low" | "ok" | "high";
  };
  content_check: {
    completeness: "ok" | "truncated";
    hooks_natural: boolean;
    closure_natural: boolean;
  };
  summary: string;
  fixes_suggested: string[];
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
}

interface LlmAuditInput {
  nodeId: string;
  h1_title: string;
  primary_entity: string;
  canonical_question: string;
  document_role: string;
  word_count: number;
  h2_count: number;
  h3_count: number;
  date_published: string;
  seo_title: string;
  meta_description: string;
  kg_entities: string;
  voice_style: string;
  evidence_style: string;
  claim_strength: string;
  article_text: string;
  internal_links: string;
}

const JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  properties: {
    overall_status: { type: "string", enum: ["passed", "warning", "failed"] },
    semantic_check: {
      type: "object",
      additionalProperties: false,
      properties: {
        stays_on_topic: { type: "boolean" },
        halucinations: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            properties: {
              claim: { type: "string" },
              type: {
                type: "string",
                enum: [
                  "date_attribution",
                  "name_invention",
                  "metric_fabrication",
                  "quote_attribution",
                ],
              },
              severity: { type: "string", enum: ["high", "medium", "low"] },
            },
            required: ["claim", "type", "severity"],
          },
        },
        voice_natural: { type: "boolean" },
        voice_issues: {
          type: "array",
          items: {
            type: "string",
            enum: [
              "ai_vocabulary",
              "repetitive_openers",
              "uniform_paragraph_rhythm",
              "clarification_negation_pairs",
              "promotional_inflation",
            ],
          },
        },
        claim_density: { type: "string", enum: ["low", "ok", "high"] },
      },
      required: [
        "stays_on_topic",
        "halucinations",
        "voice_natural",
        "voice_issues",
        "claim_density",
      ],
    },
    content_check: {
      type: "object",
      additionalProperties: false,
      properties: {
        completeness: { type: "string", enum: ["ok", "truncated"] },
        hooks_natural: { type: "boolean" },
        closure_natural: { type: "boolean" },
      },
      required: ["completeness", "hooks_natural", "closure_natural"],
    },
    summary: { type: "string" },
    fixes_suggested: { type: "array", items: { type: "string" } },
  },
  required: ["overall_status", "semantic_check", "content_check", "summary", "fixes_suggested"],
};

function stripHtmlSimple(html: string): string {
  return html.replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function truncateText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars);
  const lastSentence = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("? "), slice.lastIndexOf("! "));
  if (lastSentence > maxChars * 0.7) return slice.slice(0, lastSentence + 1).trim();
  return slice.replace(/\s+\S*$/, "");
}

async function fetchLlmAuditInput(nodeId: string): Promise<LlmAuditInput | null> {
  const { data: node } = await (supabase as any)
    .from("thematic_nodes")
    .select("title, canonical_question, document_role, primary_entity_raw, map_id")
    .eq("id", nodeId)
    .single();
  if (!node) return null;

  const { data: content } = await (supabase as any)
    .from("thematic_node_content")
    .select(
      "final_html, seo_title, meta_description, published_at, effective_publish_date",
    )
    .eq("node_id", nodeId)
    .single();
  if (!content?.final_html) return null;

  const articleText = truncateText(stripHtmlSimple(content.final_html), 3500);

  // Word/H count
  const stripped = stripHtmlSimple(content.final_html);
  const word_count = stripped ? stripped.split(/\s+/).length : 0;
  const h2_count = (content.final_html.match(/<h2\b/gi) || []).length;
  const h3_count = (content.final_html.match(/<h3\b/gi) || []).length;

  // KG entities z content_knowledge_graphs (sortowane po strength desc)
  let kg_entities = "(brak KG)";
  const { data: kg } = await (supabase as any)
    .from("content_knowledge_graphs")
    .select("kg_data")
    .eq("node_id", nodeId)
    .maybeSingle();
  if (kg?.kg_data) {
    const data = kg.kg_data as Record<string, unknown>;
    const entitiesArr = Array.isArray(data.entities) ? (data.entities as unknown[]) : [];
    const names = entitiesArr
      .map((e) => {
        if (!e || typeof e !== "object") return null;
        const o = e as Record<string, unknown>;
        return (o.entity_name || o.name || o.canonical_name) as string | null;
      })
      .filter((s): s is string => !!s)
      .slice(0, 30);
    if (names.length) kg_entities = names.join(", ");
  }

  // Writing profile (voice) — kolumna 'voice_style' NIE istnieje w schemie.
  // Real schema: voice_pronouns, verbosity, sentence_length, voice_characteristics (jsonb).
  let voice_style = "(brak profile)";
  let evidence_style = "";
  let claim_strength = "";
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("active_profile_id")
    .eq("id", node.map_id)
    .maybeSingle();
  if (map?.active_profile_id) {
    const { data: profile } = await (supabase as any)
      .from("thematic_writing_profiles")
      .select("voice_pronouns, verbosity, sentence_length, voice_characteristics, evidence_style, claim_strength")
      .eq("id", map.active_profile_id)
      .maybeSingle();
    if (profile) {
      const parts: string[] = [];
      if (profile.voice_pronouns) parts.push(`pronouns: ${profile.voice_pronouns}`);
      if (profile.verbosity) parts.push(`verbosity: ${profile.verbosity}`);
      if (profile.sentence_length) parts.push(`sentence_length: ${profile.sentence_length}`);
      if (profile.voice_characteristics && typeof profile.voice_characteristics === "object") {
        const vcKeys = Object.keys(profile.voice_characteristics as Record<string, unknown>).slice(0, 5);
        if (vcKeys.length > 0) parts.push(`characteristics: ${vcKeys.join(", ")}`);
      }
      voice_style = parts.length > 0 ? parts.join(" | ") : voice_style;
      evidence_style = profile.evidence_style || "";
      claim_strength = profile.claim_strength || "";
    }
  }

  // Internal links — wyciągnij z final_html (a href domain match nie sprawdzamy tutaj, layer 2 to robi)
  const linkMatches = [...content.final_html.matchAll(/<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi)];
  const internalLinks = linkMatches
    .slice(0, 10)
    .map((m) => `${m[2].replace(/<[^>]*>/g, "").trim().slice(0, 60)} → ${m[1]}`)
    .join(" | ");

  const date_published =
    content.effective_publish_date || content.published_at || new Date().toISOString();

  return {
    nodeId,
    h1_title: node.title || "",
    primary_entity: node.primary_entity_raw || "",
    canonical_question: node.canonical_question || "",
    document_role: node.document_role || "definition",
    word_count,
    h2_count,
    h3_count,
    date_published,
    seo_title: content.seo_title || "",
    meta_description: content.meta_description || "",
    kg_entities,
    voice_style,
    evidence_style,
    claim_strength,
    article_text: articleText,
    internal_links: internalLinks || "(brak linków wew.)",
  };
}

export async function auditLlm(nodeId: string): Promise<LlmAuditResult | null> {
  const input = await fetchLlmAuditInput(nodeId);
  if (!input) {
    logDebug("AUDIT_LLM", `Brak danych dla node ${nodeId}`);
    return null;
  }

  const loaded = await loadPrompt("content_audit_llm");
  if (!loaded?.template) {
    throw new Error("Brak promptu content_audit_llm w DB");
  }

  const prompt = fillTemplate(loaded.template, {
    h1_title: input.h1_title,
    primary_entity: input.primary_entity,
    canonical_question: input.canonical_question,
    document_role: input.document_role,
    word_count: String(input.word_count),
    h2_count: String(input.h2_count),
    h3_count: String(input.h3_count),
    date_published: input.date_published,
    seo_title: input.seo_title,
    meta_description: input.meta_description,
    kg_entities: input.kg_entities,
    voice_style: input.voice_style,
    evidence_style: input.evidence_style,
    claim_strength: input.claim_strength,
    article_text: input.article_text,
    internal_links: input.internal_links,
  });

  logDebug("AUDIT_LLM", `Wywołanie LLM audytu dla node ${nodeId}`);

  // model_id z DB steruje modelem (od 2026-08-23) — wcześniej callLLMForStage brał
  // wyłącznie DEFAULT_STAGE_MODELS i zmiana modelu w panelu nic tu nie robiła.
  // Wartość zamrożona migracją = dotychczasowy default (gpt-4.1-mini) → zero zmiany zachowania.
  const modelOverride = loaded.modelId
    ? { content_audit_llm: { provider: (loaded.modelId.startsWith("claude") ? "claude" : "openai") as "claude" | "openai", model: loaded.modelId } }
    : undefined;

  const response = await callLLMForStage("content_audit_llm", prompt, {
    nodeId,
    step: "content_audit_llm",
    temperature: loaded.temperature ?? 0.0,
    maxTokens: 1200,
    jsonSchema: JSON_SCHEMA,
  }, modelOverride);

  const parsed = parseLLMJSON<{
    overall_status: "passed" | "warning" | "failed";
    semantic_check: LlmAuditResult["semantic_check"];
    content_check: LlmAuditResult["content_check"];
    summary: string;
    fixes_suggested: string[];
  }>(response.text);

  logPublic(
    "AUDIT_LLM",
    `OK node ${nodeId}: status=${parsed.overall_status}, halucynacje=${parsed.semantic_check.halucinations.length}, cost=$${(response.costUsd ?? 0).toFixed(4)}`,
  );

  // Map overall_status do status (ok/warning/failed)
  const status: LlmAuditResult["status"] =
    parsed.overall_status === "passed"
      ? "ok"
      : parsed.overall_status === "warning"
        ? "warning"
        : "failed";

  return {
    status,
    overall_status: parsed.overall_status,
    semantic_check: parsed.semantic_check,
    content_check: parsed.content_check,
    summary: parsed.summary,
    fixes_suggested: parsed.fixes_suggested,
    cost_usd: response.costUsd ?? 0,
    tokens_in: response.tokensInput ?? 0,
    tokens_out: response.tokensOutput ?? 0,
  };
}
